import { api } from '../services/api';
import { db, marcarSincronizado, ultimaSincronizacao } from './db';
import { SYNC_SCHEMA } from './schema';
import { ehUuid } from './uuid';

// --- Pull: baixa o que mudou no servidor desde a última sincronização ---

function idParaUuidLocal(tabelaPai, idServidor) {
  if (idServidor === null || idServidor === undefined) return null;
  return db[tabelaPai].where('id').equals(idServidor).first().then((registro) => registro?.uuid ?? null);
}

function valorFkNaLeitura(registroServidor, campo) {
  // `campo` é o nome usado na ESCRITA (payload de criar/atualizar), que é o
  // que importa pra resolução no push. Vários serializers usam um nome
  // diferente na LEITURA do mesmo FK: `categoria_principal_id` (escrita) vs
  // `categoria_principal` (leitura, aninhado); ou pior, quando o campo de
  // escrita é `write_only=True` (ex.: MovimentacaoEstoqueSerializer.item),
  // ele nem aparece na leitura — só a versão `_detalhe`. Tenta os nomes
  // candidatos mais comuns antes de desistir.
  const semSufixoId = campo.endsWith('_id') ? campo.slice(0, -3) : campo;
  const candidatos = [campo, semSufixoId, `${campo}_detalhe`, `${semSufixoId}_detalhe`];
  for (const nome of candidatos) {
    if (registroServidor[nome] !== undefined) return registroServidor[nome];
  }
  return undefined;
}

async function normalizarParaLocal(tabela, registroServidor) {
  const { fkUuid } = SYNC_SCHEMA[tabela];
  const local = { ...registroServidor };

  for (const { campo, tabela: tabelaPai } of fkUuid) {
    const valor = valorFkNaLeitura(registroServidor, campo);
    const idServidor = valor && typeof valor === 'object' ? valor.id : valor;
    local[campo] = typeof idServidor === 'number' ? await idParaUuidLocal(tabelaPai, idServidor) : idServidor;
  }

  return local;
}

export async function puxarTabela(chave) {
  const { tabela, endpoint } = SYNC_SCHEMA[chave];
  const desde = await ultimaSincronizacao(tabela);
  const query = desde ? `?since=${encodeURIComponent(desde)}` : '';

  const resposta = await api.get(`${endpoint}/sync/${query}`);
  const { servidor_em, resultados } = resposta.data;

  for (const registroServidor of resultados) {
    const local = await normalizarParaLocal(chave, registroServidor);
    if (registroServidor.excluido_em) {
      await db[tabela].delete(local.uuid);
    } else {
      await db[tabela].put(local);
    }
    await db.uuid_map.put({ uuid: local.uuid, tabela: chave, status: 'synced' });
  }

  await marcarSincronizado(tabela, servidor_em);
  return resultados.length;
}

export async function puxarTudo({ onProgresso } = {}) {
  let total = 0;
  for (const chave of Object.keys(SYNC_SCHEMA)) {
    const quantidade = await puxarTabela(chave);
    total += quantidade;
    onProgresso?.(chave, quantidade);
  }
  return total;
}

// --- Formatação para a tela: inverso de normalizarParaLocal ---
//
// Registros no Dexie guardam FK como uuid do pai (ver topo do arquivo) e
// podem não ter `id` de servidor ainda (registro criado offline). Para que
// um <select> populado por `listar()` bata com o valor de FK de um registro
// também vindo de `listar()`, os dois lados precisam usar a MESMA regra:
// id real se o registro já sincronizou, uuid caso contrário. É essa regra
// que esta função aplica antes de devolver dados para a página.
export async function formatarParaTela(chave, registro) {
  const { fkUuid } = SYNC_SCHEMA[chave];
  const exibicao = { ...registro, id: registro.id ?? registro.uuid, _pendente: registro.id == null };

  for (const { campo, tabela: tabelaPai } of fkUuid) {
    const uuidPai = registro[campo];
    if (!uuidPai || !ehUuid(uuidPai)) continue; // já é id real (ou vazio) — nada a fazer
    const registroPai = await db[SYNC_SCHEMA[tabelaPai].tabela].get(uuidPai);
    exibicao[campo] = registroPai ? (registroPai.id ?? registroPai.uuid) : uuidPai;
  }

  return exibicao;
}

// --- Enfileirar mudança local (chamado pelos repositórios) ---

export async function enfileirarMudanca({ chave, op, uuid, payload, baseAtualizadoEm }) {
  const { tabela, escritaOffline } = SYNC_SCHEMA[chave];
  if (!escritaOffline) {
    throw new Error(`'${chave}' não aceita escrita offline — exige conexão.`);
  }

  const agora = new Date().toISOString();

  if (op === 'delete') {
    await db[tabela].update(uuid, { excluido_em: agora });
  } else {
    await db[tabela].put({ ...payload, uuid, atualizado_em: agora });
  }

  await db.uuid_map.put({ uuid, tabela: chave, status: 'pending' });
  await db.sync_queue.add({
    tabela: chave,
    record_uuid: uuid,
    op,
    payload,
    client_edited_at: agora,
    base_atualizado_em: baseAtualizadoEm ?? null,
    status: 'pending',
    tentativas: 0,
    criado_em: agora,
  });
}

// --- Push: drena a fila para o servidor ---

async function resolverPayloadParaEnvio(chave, payload) {
  const { fkUuid } = SYNC_SCHEMA[chave];
  const resolvido = { ...payload };

  for (const { campo, tabela: tabelaPai } of fkUuid) {
    const uuidPai = payload[campo];
    // Já é um id real de servidor (ex.: escolhido num <select> cujas opções
    // já estavam sincronizadas) — nada a resolver, envia como está.
    if (!uuidPai || !ehUuid(uuidPai)) continue;

    const statusPai = await db.uuid_map.get(uuidPai);
    if (!statusPai || statusPai.status !== 'synced') {
      return { bloqueado: true, motivo: `depende de '${campo}' ainda não sincronizado` };
    }

    const registroPai = await db[SYNC_SCHEMA[tabelaPai].tabela].get(uuidPai);
    if (!registroPai?.id) {
      return { bloqueado: true, motivo: `'${campo}' sem id de servidor ainda` };
    }
    resolvido[campo] = registroPai.id;
  }

  return { bloqueado: false, payload: resolvido };
}

const BACKOFF_MS = [5000, 20000, 60000, 300000];

export async function drenarFila({ onEntrada } = {}) {
  const pendentes = await db.sync_queue.where('status').equals('pending').sortBy('queue_id');
  const bloqueadosNesteCiclo = new Set();

  for (const entrada of pendentes) {
    if (bloqueadosNesteCiclo.has(entrada.record_uuid)) continue;

    const { bloqueado, payload, motivo } = await resolverPayloadParaEnvio(entrada.tabela, entrada.payload);
    if (bloqueado) {
      bloqueadosNesteCiclo.add(entrada.record_uuid);
      onEntrada?.(entrada, 'bloqueado', motivo);
      continue;
    }

    const { modelo } = SYNC_SCHEMA[entrada.tabela];
    try {
      const resposta = await api.post('/api/sync/push/', {
        changes: [{
          uuid: entrada.record_uuid,
          model: modelo,
          op: entrada.op,
          payload,
          client_edited_at: entrada.client_edited_at,
          base_atualizado_em: entrada.base_atualizado_em,
        }],
      });

      const resultado = resposta.data.resultados[0];
      if (resultado.status === 'ok') {
        await db.uuid_map.update(entrada.record_uuid, { status: 'synced' });
        if (resultado.id) {
          await db[SYNC_SCHEMA[entrada.tabela].tabela].update(entrada.record_uuid, {
            id: resultado.id,
            atualizado_em: resultado.servidor_atualizado_em ?? entrada.client_edited_at,
          });
        }
        await db.sync_queue.delete(entrada.queue_id);
        onEntrada?.(entrada, 'ok');
      } else {
        // conflito ou erro: fica registrado, não trava a fila — parked.
        const detalhe = resultado.detail ?? resultado.status;
        const tipo_falha = typeof detalhe === 'string' && detalhe.includes('Sem permissão')
          ? 'permissao'
          : 'validacao';
        await db.sync_queue.update(entrada.queue_id, { status: 'failed', detalhe, tipo_falha });
        onEntrada?.(entrada, 'erro', detalhe);
      }
    } catch (erro) {
      const tentativas = entrada.tentativas + 1;
      if (tentativas >= BACKOFF_MS.length) {
        await db.sync_queue.update(entrada.queue_id, { status: 'failed', tentativas, detalhe: String(erro), tipo_falha: 'rede' });
      } else {
        await db.sync_queue.update(entrada.queue_id, { tentativas });
      }
      bloqueadosNesteCiclo.add(entrada.record_uuid);
      onEntrada?.(entrada, 'falha_rede', String(erro));
    }
  }
}

export async function contarPendentes() {
  return db.sync_queue.where('status').equals('pending').count();
}

export async function listarFalhadas() {
  return db.sync_queue.where('status').equals('failed').sortBy('queue_id');
}

export async function requeueFalhadas(queueIds) {
  for (const id of queueIds) {
    await db.sync_queue.update(id, { status: 'pending', tentativas: 0, detalhe: null, tipo_falha: null });
  }
}

export async function requeuePermissoes() {
  const falhadas = await db.sync_queue.where('tipo_falha').equals('permissao').toArray();
  const ids = falhadas.map((e) => e.queue_id);
  await requeueFalhadas(ids);
  return ids.length;
}
